import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { createSSRSearchClient } from 'angular-instantsearch';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { LocaleService } from '@app/core/i18n';
import { environment } from '@env/environment';
import { SearchConfig, SearchFunction, SearchHelper, SearchParameters } from '@app/core/search/common';
import { SearchRoutingService, SearchStateMappingService } from '@app/core/search/services';

export enum SuggestionsIndex {
  SuggestionsEN = 'search_en_query_suggestions',
  SuggestionsES = 'search_es_query_suggestions',
  SuggestionsDE = 'search_de_query_suggestions',
  SuggestionsFR = 'search_fr_query_suggestions',
  SuggestionIT = 'search_it_query_suggestions',
  SuggestionDefault = 'search_query_suggestions'
}

export enum SearchIndex {
  Default = 'ng-products-master',
  DefaultUs = 'ng-products-slave-us', // Custom sort if the user has set its country as United States
  NewIn = 'ng-products-slave-recent',
  Recent = 'ng-products-slave-recent',
  PriceAsc = 'ng-products-slave-price-asc',
  PriceDesc = 'ng-products-slave-price-desc',
  Brands = 'brands'
}

@Injectable()
export class InstantSearchService {
  private countryCode: string;
  private currencyCode: string;
  private translationKey: string;

  constructor(
    private localeService: LocaleService,
    private httpClient: HttpClient,
    private transferState: TransferState,
    private searchStateMapper: SearchStateMappingService,
    private searchRouter: SearchRoutingService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.countryCode = localeService.getIsoCodeForSize(localeService.getCountry());
    this.currencyCode = localeService.getCurrency();
    this.translationKey = localeService.getTranslationKey();
  }

  /**
   * @param indexName
   * @param searchFunction
   * @param searchParameters parameters to pass to the Algolia Search API
   * @param routing Enable InstantSearch routing option
   */
  createConfig(
    indexName: string,
    searchFunction?: SearchFunction,
    searchParameters?: SearchParameters,
    routing?: boolean
  ): SearchConfig {
    if (typeof searchFunction === 'undefined') {
      searchFunction = (helper: SearchHelper) => helper.search();
    }

    searchParameters = this.setSearchParameters(searchParameters);

    let options = {};

    if (environment.algoliaProxyHost) {
      options = {
        hosts: {
          read: { url: environment.algoliaProxyHost },
          write: { url: environment.algoliaProxyHost }
        }
      };
    }

    const config: SearchConfig = {
      indexName,
      searchFunction,
      searchParameters,
      searchClient: createSSRSearchClient({
        appId: environment.algoliaAppId,
        apiKey: environment.algoliaApiKey,
        httpClient: this.httpClient,
        HttpHeaders,
        transferState: this.transferState,
        makeStateKey,
        options
      }),
      routing: routing
        ? {
            router: this.searchRouter,
            stateMapping: this.searchStateMapper
          }
        : null
    };

    return config;
  }

  setSearchParameters(searchParameters: SearchParameters): SearchParameters {
    if (isPlatformServer(this.platformId)) {
      const routeState = this.searchRouter.read();
      const uiState = this.searchStateMapper.routeToState(routeState);

      // On server side, only page is transmitted.
      // Keep in mind that search state is read from the URL fragment (hash).
      if (uiState.page && uiState.page > 0) {
        // IMPORTANT NOTICE:
        // In the API page numbers are zero-based. Therefore, in order
        // to retrieve the 10th page, you need to set `page=9`.
        searchParameters.page = uiState.page - 1;
        this.searchRouter.write(routeState);
      }
    }

    return { ...this.getDefaultSearchParameters(), ...searchParameters };
  }

  getAttributes(): string[] {
    return [
      'id',
      'brand.id',
      'brand.searchable',
      `name.searchable.${this.translationKey}`,
      `price.cents.${this.currencyCode}`,
      `discount.originalPriceCents.${this.currencyCode}`,
      `link.${this.translationKey}`,
      'pictures',
      'sold.sold',
      'stock',
      'tags',
      'likes.count',
      'editorPicks',
      'size',
      'shouldBeGone',
      'country',
      'seller.firstname',
      `seller.badge.${this.translationKey}`,
      'seller.isOfficialStore',
      'dsEligible',
      'dsEligibleCountries',
      'localCountries',
      'personalized',
      'colors.facet',
      'colors.originalID'
    ];
  }

  /**
   * getDisjunctiveFacets returns the list of disjunctive facets
   * @returns {string[]}
   * @memberof InstantSearchService
   */
  getDisjunctiveFacets(): string[] {
    const facets = [
      'brand.facet',
      'model.facet',
      `universe.facet.${this.translationKey}`,
      `colors.facet.${this.translationKey}`,
      `price.range.${this.currencyCode}`,
      `condition.facet.${this.translationKey}`,
      `price.cents.${this.currencyCode}`,
      `region.facet.${this.translationKey}`,
      `editorPicks.facet.${this.translationKey}`,
      `watchMechanism.facet.${this.translationKey}`,
      'discount.ranges',
      'sold.sold',
      'stock',
      'country',
      'dsEligible',
      'dsEligibleCountries',
      'localcountries',
      'editorPicks.dealEligible'
    ];

    for (const id of Array.from(Array(25).keys())) {
      facets.push(`size.facet.type${id}.${this.countryCode}`);
    }

    return facets;
  }

  /**
   * Returns a complete list of attributes that will be used for searching.
   * This allows the restriction of search to a subset of localized attributes
   * and helps preventing search issues due to multilingualism.
   */
  getSearchableAttributes(): string[] {
    return [
      'model.searchable',
      'brand.searchable',
      `category.searchable.${this.translationKey}`,
      `subcategory.searchable.${this.translationKey}`,
      `colors.searchable.${this.translationKey}`,
      `name.searchable.${this.translationKey}`,
      `season.searchable.${this.translationKey}`,
      `materials.searchable.${this.translationKey}`,
      `universe.searchable.${this.translationKey}`,
      `description.searchable.${this.translationKey}`
    ];
  }

  getDefaultSearchParameters(): SearchParameters {
    return {
      hitsPerPage: 48,
      maxValuesPerFacet: 100,
      attributesToRetrieve: this.getAttributes(),
      restrictSearchableAttributes: this.getSearchableAttributes(),
      disjunctiveFacets: this.getDisjunctiveFacets(),
      analytics: true,
      clickAnalytics: true,
      getRankingInfo: true,
      analyticsTags: ['web'],
      ruleContexts: [this.localeService.getCountry()]
    };
  }

  getCurrentRefinementOnly(): { name: string; label: string }[] {
    const currentRefinements = [
      {
        name: `universe.facet.${this.translationKey}`,
        label: 'universe.facet.' + this.translationKey
      },
      {
        name: `category.facet.${this.translationKey}.lvl0`,
        label: `category.facet.${this.translationKey}.lvl0`
      },
      {
        name: `category.facet.${this.translationKey}.lvl1`,
        label: `category.facet.${this.translationKey}.lvl1`
      },
      {
        name: `editorPicks.facet.${this.translationKey}`,
        label: `editorPicks.facet.${this.translationKey}`
      },
      {
        name: 'brand.facet',
        label: 'brand.facet'
      },
      {
        name: 'discount.ranges',
        label: 'discount.ranges'
      },
      {
        name: 'model.facet',
        label: 'model.facet'
      },
      {
        name: `seller.badge.${this.translationKey}`,
        label: `seller.badge.${this.translationKey}`
      },
      {
        name: 'seller.isOfficialStore',
        label: 'seller.isOfficialStore'
      },
      {
        name: `colors.facet.${this.translationKey}`,
        label: `colors.facet.${this.translationKey}`
      },
      {
        name: `materials.facet.${this.translationKey}.lvl0`,
        label: `materials.facet.${this.translationKey}.lvl0`
      },
      {
        name: `materials.facet.${this.translationKey}.lvl1`,
        label: `materials.facet.${this.translationKey}.lvl1`
      },
      {
        name: `price.cents.${this.currencyCode}`,
        label: `price.cents.${this.currencyCode}`
      },
      {
        name: `condition.facet.${this.translationKey}`,
        label: `condition.facet.${this.translationKey}`
      },
      {
        name: `region.facet.${this.translationKey}`,
        label: `region.facet.${this.translationKey}`
      },
      {
        name: `season.facet.${this.translationKey}`,
        label: `season.facet.${this.translationKey}`
      },
      {
        name: `watchMechanism.facet.${this.translationKey}`,
        label: `watchMechanism.facet.${this.translationKey}`
      }
    ];

    // adding 25 differents size types...
    for (const id of Array.from(Array(25).keys())) {
      currentRefinements.push({
        name: `size.facet.type${id}.${this.countryCode}`,
        label: `size.facet.type${id}.${this.countryCode}`
      });
    }

    currentRefinements.push(
      { name: 'sold.sold', label: 'sold.sold' },
      { name: 'stock', label: 'stock' },
      { name: 'dsEligible', label: 'dsEligible' },
      { name: 'dsEligibleCountries', label: 'dsEligibleCountries' },
      { name: 'localCountries', label: 'localCountries' },
      { name: 'editorPicks.dealEligible', label: 'editorPicks.dealEligible' },
      { name: 'country', label: 'Shipped from' }
    );

    return currentRefinements;
  }
}
